import { useEffect, useState } from "react";
import { api, fmtPeso, fmtNum, currentMonth } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";
import { Ship, FileText, Wallet, TrendingUp, DollarSign, FileSpreadsheet, FileDown, Award, TrendingDown } from "lucide-react";
import CostoProveedores from "@/pages/CostoProveedores";

const Kpi = ({ label, value, sub, icon: Icon, accent }) => (
  <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5" data-testid={`kpi-${label}`}>
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium">{label}</p>
      <div className={`h-8 w-8 rounded-md flex items-center justify-center ${accent}`}>
        <Icon className="h-4 w-4" strokeWidth={2} />
      </div>
    </div>
    <p className="mt-3 font-heading text-2xl font-bold tracking-tight text-slate-900 tabular-nums">{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

export default function Dashboard() {
  const [mes, setMes] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [estado, setEstado] = useState(null);
  const [comparativo, setComparativo] = useState(null);
  const [tab, setTab] = useState("estado");

  const load = () => {
    api.get(`/dashboard/summary?mes=${mes}`).then((r) => setData(r.data));
    api.get(`/estado-resultados?mes=${mes}`).then((r) => setEstado(r.data));
    const [yy, mm] = mes.split("-");
    api.get(`/reportes/costo-proveedores?mes=${Number(mm)}&anio=${Number(yy)}`)
      .then((r) => setComparativo(r.data))
      .catch(() => setComparativo(null));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [mes]);

  const k = data?.kpis || {};

  const provsCmp = comparativo?.providers || [];
  const costoRow = (comparativo?.rows || []).find((r) => r.key === "molecula_importadora_pk");
  const cmpData = provsCmp.map((p, i) => ({ proveedor: p, costo: Number(costoRow?.values?.[i] || 0) }));
  const costos = cmpData.map((d) => d.costo);
  const minCosto = costos.length ? Math.min(...costos) : 0;
  const maxCosto = costos.length ? Math.max(...costos) : 0;
  const hasCmp = cmpData.length > 0 && maxCosto > 0;
  const mejor = hasCmp ? cmpData.reduce((a, b) => (b.costo < a.costo ? b : a)) : null;
  const peor = hasCmp ? cmpData.reduce((a, b) => (b.costo > a.costo ? b : a)) : null;
  const fmtCosto = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 5, maximumFractionDigits: 5 })}`;

  const exportar = async (formato) => {
    try {
      const res = await api.get(`/estado-resultados/export?mes=${mes}&formato=${formato}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `estado_resultados_${mes}.${formato === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Estado de Resultados exportado (${formato === "excel" ? "Excel" : "PDF"})`);
    } catch (e) {
      toast.error("No se pudo exportar el reporte");
    }
  };

  const tabsBar = (
    <div className="inline-flex items-center gap-1 rounded-lg bg-slate-100 p-1">
      <button data-testid="tab-dashboard-estado" onClick={() => setTab("estado")}
        className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${tab === "estado" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
        Estado de Resultados
      </button>
      <button data-testid="tab-dashboard-reporte" onClick={() => setTab("reporte")}
        className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${tab === "reporte" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
        Reporte
      </button>
    </div>
  );

  if (tab === "reporte") {
    return (
      <div className="space-y-6">
        {tabsBar}
        <CostoProveedores />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {tabsBar}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-slate-500 text-sm">Consolidado automático de costos y presupuesto del periodo.</p>
        <div className="flex items-center gap-2">
          <button
            data-testid="export-excel"
            onClick={() => exportar("excel")}
            className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Excel
          </button>
          <button
            data-testid="export-pdf"
            onClick={() => exportar("pdf")}
            className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <FileDown className="h-4 w-4 text-red-600" /> PDF
          </button>
          <input
            data-testid="dashboard-month"
            type="month"
            value={mes}
            onChange={(e) => setMes(e.target.value)}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Presupuesto" value={fmtPeso(estado?.total_presupuesto)} sub="Total planeado" icon={Wallet} accent="bg-blue-50 text-blue-600" />
        <Kpi label="Ejercido" value={fmtPeso(estado?.total_ejercido)} sub="Costos reales" icon={TrendingUp} accent="bg-amber-50 text-amber-600" />
        <Kpi
          label="Diferencia"
          value={fmtPeso(estado?.diferencia)}
          sub={estado?.diferencia >= 0 ? "Dentro de presupuesto" : "Excedido"}
          icon={DollarSign}
          accent={estado?.diferencia >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}
        />
        <Kpi
          label="Tipo de cambio DOF"
          value={k.tipo_cambio ? fmtNum(k.tipo_cambio) : "—"}
          sub={k.tipo_cambio_fecha ? `Al ${k.tipo_cambio_fecha}` : "Sin registro"}
          icon={DollarSign}
          accent="bg-slate-100 text-slate-700"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-slate-900">Costos aduanales por día</h3>
          </div>
          <div className="p-4 h-72">
            {data?.serie_aduanas?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.serie_aduanas}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                  <XAxis dataKey="fecha" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => v.slice(8)} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                  <Tooltip formatter={(v) => fmtPeso(v)} />
                  <Bar dataKey="monto" fill="#0f172a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                Sin movimientos aduanales este mes
              </div>
            )}
          </div>
        </div>

        {/* Estado de resultados breakdown */}
        <div className="bg-white border border-slate-200 rounded-md shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-slate-900">Estado de Resultados</h3>
          </div>
          <div className="p-6 space-y-4">
            {(estado?.categorias || []).map((c) => (
              <div key={c.nombre} className="flex items-center justify-between">
                <span className="text-sm text-slate-600">{c.nombre}</span>
                <span className="text-sm font-medium tabular-nums text-slate-900">{fmtPeso(c.ejercido)}</span>
              </div>
            ))}
            <div className="border-t border-slate-100 pt-4 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-900">Total Ejercido</span>
              <span className="text-sm font-bold tabular-nums text-slate-900">{fmtPeso(estado?.total_ejercido)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Presupuestado</span>
              <span className="text-sm tabular-nums text-slate-900">{fmtPeso(estado?.total_presupuesto)}</span>
            </div>
            <div
              className={`flex items-center justify-between rounded-md px-3 py-2 ${
                estado?.diferencia >= 0 ? "bg-emerald-50" : "bg-red-50"
              }`}
            >
              <span className="text-sm font-semibold text-slate-900">Diferencia</span>
              <span
                className={`text-sm font-bold tabular-nums ${
                  estado?.diferencia >= 0 ? "text-emerald-700" : "text-red-700"
                }`}
              >
                {fmtPeso(estado?.diferencia)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Conteos */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-3">
          <Ship className="h-5 w-5 text-slate-400" />
          <div>
            <p className="font-heading text-lg font-bold text-slate-900">{data?.conteos?.movimientos_aduana ?? 0}</p>
            <p className="text-xs text-slate-500">Movimientos aduana</p>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-3">
          <FileText className="h-5 w-5 text-slate-400" />
          <div>
            <p className="font-heading text-lg font-bold text-slate-900">{data?.conteos?.facturas ?? 0}</p>
            <p className="text-xs text-slate-500">Facturas del mes</p>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-3">
          <TrendingUp className="h-5 w-5 text-slate-400" />
          <div>
            <p className="font-heading text-lg font-bold text-slate-900">{data?.conteos?.registros_precio ?? 0}</p>
            <p className="text-xs text-slate-500">Registros de precio</p>
          </div>
        </div>
      </div>

      {/* Comparativo de costo por proveedor */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm" data-testid="comparativo-proveedores">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-heading font-semibold text-slate-900">Comparativo de costo por proveedor</h3>
          <span className="text-xs text-slate-400">Costo final molécula (P/K) · {comparativo?.titulo || ""}</span>
        </div>
        {!hasCmp ? (
          <div className="p-10 text-center text-slate-400 text-sm" data-testid="comparativo-sin-datos">
            Sin datos suficientes para comparar este periodo.
          </div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3" data-testid="comparativo-mejor">
                <div className="h-10 w-10 rounded-md bg-emerald-600 text-white flex items-center justify-center">
                  <Award className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.12em] text-emerald-700 font-medium">Más económico</p>
                  <p className="font-heading text-lg font-bold text-emerald-900">{mejor?.proveedor}</p>
                  <p className="text-sm tabular-nums text-emerald-800">{fmtCosto(mejor?.costo)}</p>
                </div>
              </div>
              <div className="rounded-md border border-red-200 bg-red-50 p-4 flex items-center gap-3" data-testid="comparativo-peor">
                <div className="h-10 w-10 rounded-md bg-red-600 text-white flex items-center justify-center">
                  <TrendingDown className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.12em] text-red-700 font-medium">Más costoso</p>
                  <p className="font-heading text-lg font-bold text-red-900">{peor?.proveedor}</p>
                  <p className="text-sm tabular-nums text-red-800">{fmtCosto(peor?.costo)}</p>
                </div>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cmpData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                  <XAxis dataKey="proveedor" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-12} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => v.toFixed(2)} />
                  <Tooltip formatter={(v) => fmtCosto(v)} />
                  <Bar dataKey="costo" radius={[4, 4, 0, 0]}>
                    {cmpData.map((d) => (
                      <Cell key={d.proveedor} fill={maxCosto > minCosto && d.costo === minCosto ? "#059669" : maxCosto > minCosto && d.costo === maxCosto ? "#dc2626" : "#1F3A5F"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
